'use client';

/**
 * WebQAGroundingCard — Reception "grounding & cost" lens.
 *
 * Reads `useDocsStore.response`: the confidence chip, the documents the
 * answer drew on (from `sources` for now — until the response carries an
 * explicit grounded-docs list), token usage (input/output/total — the
 * input count ≈ what was uploaded to Gemini), `total_ms`, and the
 * per-phase timings. Makes one answer's cost visible on screen; the
 * aggregate version belongs in the system-obeya analytics surface.
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { useDocsStore } from '@/lib/stores/docs-store';
import { useCardTypography } from '@/lib/design/card-chrome';
import { cn } from '@/lib/utils/cn';
import { QA_SECTION, QA_BOX } from './web-qa-chrome';
import { QASkeleton } from './web-qa-skeleton';


const CONFIDENCE_COLOR: Record<string, string> = {
  high: 'bg-green-500/15 text-green-700 dark:text-green-400 border-green-500/30',
  medium: 'bg-yellow-500/15 text-yellow-700 dark:text-yellow-400 border-yellow-500/30',
  low: 'bg-orange-500/15 text-orange-700 dark:text-orange-400 border-orange-500/30',
  none: 'bg-muted text-muted-foreground border-border',
};

const fmt = (n: number | undefined | null): string =>
  typeof n === 'number' ? n.toLocaleString() : '—';


export function WebQAGroundingCard() {
  const T = useCardTypography();
  const response = useDocsStore((s) => s.response);
  const isLoading = useDocsStore((s) => s.isLoading);

  // Grounding/cost telemetry is the first sign of life — skeleton it while the
  // fast tier is in flight rather than waiting for the whole answer.
  if (isLoading) return <QASkeleton label="Grounding & cost" lines={3} />;
  if (!response) return null;

  const u = response.usage;
  const phases = response.phase_timings ?? {};
  // The docs the synthesis LLM was actually grounded on (post-frontier-filter)
  // — not `sources`, which mixes graph/capability matches + the LLM's own
  // per-section citations (the same doc repeated). Basename, deduped.
  const grounded = Array.from(
    new Set(
      (response.grounded_docs ?? []).map((d) => d.replace(/^.*\//, '').replace(/\.md$/i, '')),
    ),
  );

  return (
    <section className={QA_SECTION}>
      <div className="w-full">
        <div className={QA_BOX}>
          <div className={cn(T.metadata, 'uppercase tracking-wider text-xs')}>
            Grounding &amp; cost
          </div>

          {/* confidence + wall time */}
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className={cn(
                'inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded-full border',
                CONFIDENCE_COLOR[response.confidence] ?? CONFIDENCE_COLOR.none,
              )}
            >
              {response.confidence === 'none' ? 'no match' : `${response.confidence} confidence`}
            </span>
            <span className={cn(T.metadata)}>
              {(response.total_ms / 1000).toFixed(1)}s · turn {response.turn}
            </span>
          </div>

          {/* tokens */}
          <div className={cn(T.body, 'text-sm grid grid-cols-3 gap-2')}>
            <div>
              <div className="text-muted-foreground text-xs">uploaded ≈ in</div>
              <div className="font-medium">{fmt(u?.input_tokens)}</div>
            </div>
            <div>
              <div className="text-muted-foreground text-xs">out</div>
              <div className="font-medium">{fmt(u?.output_tokens)}</div>
            </div>
            <div>
              <div className="text-muted-foreground text-xs">total tok</div>
              <div className="font-medium">{fmt(u?.total_tokens)}</div>
            </div>
          </div>

          {/* phase timings */}
          {Object.keys(phases).length > 0 && (
            <div className={cn(T.metadata, 'flex flex-wrap gap-x-3 gap-y-1')}>
              {Object.entries(phases).map(([k, ms]) => (
                <span key={k}>
                  {k.replace(/_ms$/, '')}: {Math.round(Number(ms))}ms
                </span>
              ))}
            </div>
          )}

          {/* docs the synthesis was grounded on (post-frontier-filter) */}
          <div>
            <div className={cn(T.metadata, 'text-xs mb-1')}>
              {grounded.length > 0
                ? `Grounded in ${grounded.length} document${grounded.length === 1 ? '' : 's'}`
                : 'No LLM grounding (fast tier)'}
            </div>
            {grounded.length > 0 && (
              <ul className={cn(T.metadata, 'flex flex-col gap-0.5 max-h-32 overflow-auto')}>
                {grounded.map((d) => (
                  <li key={d} className="text-muted-foreground">· {d}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
